
import type { TadaDocumentNode } from 'gql.tada';
import { print } from 'graphql';

export const GRAPHQL_ENDPOINT = '/api';

export interface GraphQLResponseError {
  readonly message: string;
  readonly path?: ReadonlyArray<string | number>;
  readonly extensions?: Record<string, unknown>;
}

export class GraphQLRequestError extends Error {
  readonly errors: readonly GraphQLResponseError[];
  readonly correlationId: string | undefined;

  constructor(message: string, errors: readonly GraphQLResponseError[]) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.errors = errors;
    this.correlationId = findCorrelationId(errors);
  }
}

function findCorrelationId(errors: readonly GraphQLResponseError[]): string | undefined {
  for (const e of errors) {
    const id = e.extensions?.['correlationId'];
    if (typeof id === 'string') return id;
  }
  return undefined;
}

type CallArgs<TVars> = Record<string, never> extends TVars
  ? [variables?: TVars, options?: RequestOptions]
  : [variables: TVars, options?: RequestOptions];

export interface RequestOptions {
  signal?: AbortSignal;
}

export async function gqlRequest<TResult, TVars>(
  document: TadaDocumentNode<TResult, TVars>,
  ...args: CallArgs<TVars>
): Promise<TResult> {
  const [variables, options] = args as [TVars | undefined, RequestOptions | undefined];
  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/graphql-response+json, application/json',
      'x-cheddule-request': 'graphql',
    },
    body: JSON.stringify({ query: print(document), variables: variables ?? {} }),
    signal: options?.signal,
  });

  if (!response.ok) {
    let bodyErrors: GraphQLResponseError[] = [];
    try {
      const body = (await response.json()) as { errors?: GraphQLResponseError[] };
      if (Array.isArray(body.errors)) bodyErrors = body.errors;
    } catch {
    }
    throw new GraphQLRequestError(
      bodyErrors[0]?.message ?? `GraphQL request failed (HTTP ${response.status})`,
      bodyErrors,
    );
  }

  const body = (await response.json()) as {
    data?: TResult;
    errors?: GraphQLResponseError[];
  };

  if (body.errors?.length) {
    throw new GraphQLRequestError(body.errors[0]?.message ?? 'GraphQL error', body.errors);
  }

  return body.data as TResult;
}
